import React, { useState, useEffect } from 'react';
import { CircularProgress } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import skillApis from '../../../apis/skills';
import DialogModal from '../../../components/DialogModal';
import { SnackbarSeverityType } from '../../../enums';
import pallet from '../../../layouts/pallet';
import { StyledButton, ViewEditWrapper } from '../../Exams/styled';
import { SubSkillResponse, ViewEditSkillModalProps } from '../types';
import {
  FullWidthBox,
  ResultCountText,
  ViewEditActionsContainer,
  ViewEditCancelButton,
  ViewEditContentContainer,
  ViewEditHeaderRow,
  viewEditPaperPropsSx
} from '../styled';
import { getSubSkillColumns } from '../utils/skillTableColumns';
import AddSubSkillModal from './AddSubSkillModal';
import EditSubSkillModal from './EditSubSkillModal';
import { dataGridSx } from '../../common/styled/DataGrid';
import Icons from '../../../components/icons';
import TextInputWithIcon from '../../../components/TextInputWithIcon';
import useScoringTypes from '../hooks/useScoringTypes';
import { TEMP_SUBSKILL_ID_PREFIX } from '../hooks/useSubSkillEditor';

const ViewEditSkillModal: React.FC<ViewEditSkillModalProps> = ({
  open,
  onClose,
  onSkillUpdated,
  skill,
  academicYear,
  grade,
  division,
  school,
  showToast
}) => {
  const [skillName, setSkillName] = useState('');
  const [subSkills, setSubSkills] = useState<SubSkillResponse[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isAddSubSkillModalOpen, setIsAddSubSkillModalOpen] = useState(false);
  const [isEditSubSkillModalOpen, setIsEditSubSkillModalOpen] = useState(false);
  const [subSkillToEdit, setSubSkillToEdit] = useState<SubSkillResponse | null>(
    null
  );

  const { scoringTypes, isLoading: isLoadingScoringTypes } = useScoringTypes(
    open,
    school,
    showToast
  );

  useEffect(() => {
    if (skill) {
      setSkillName(skill.skillName);
      setSubSkills(skill.subSkills || []);
    }
  }, [skill]);

  if (!skill) {
    return null;
  }

  const handleOpenAddSubSkill = () => {
    setIsAddSubSkillModalOpen(true);
  };

  const handleAddSubSkill = (subSkill: SubSkillResponse) => {
    setSubSkills((prev) => [...prev, subSkill]);
  };

  const handleOpenEditSubSkill = (subSkill: SubSkillResponse) => {
    setSubSkillToEdit(subSkill);
    setIsEditSubSkillModalOpen(true);
  };

  const handleSaveEditedSubSkill = (updatedSubSkill: SubSkillResponse) => {
    setSubSkills((prev) =>
      prev.map((s) => (s.id === updatedSubSkill.id ? updatedSubSkill : s))
    );
  };

  const handleRemoveSubSkill = (subSkillId: string) => {
    setSubSkills((prev) => prev.filter((s) => s.id !== subSkillId));
  };

  const handleSave = async () => {
    if (!school) return;

    const subSkillsPayload = subSkills.map((s) => {
      const scoringTypeId =
        typeof s.scoringType === 'string'
          ? s.scoringType
          : s.scoringType?.id || '';
      return {
        ...(s.id && !s.id.startsWith(TEMP_SUBSKILL_ID_PREFIX)
          ? { id: s.id }
          : {}),
        description: s.description,
        scoringType: scoringTypeId,
        order: s.order,
        ...(s.maxMarks != null ? { maxMarks: s.maxMarks } : {})
      };
    });

    try {
      setIsSaving(true);
      await skillApis.upsertSkill(school, grade, division, {
        id: skill.id,
        skillName,
        subSkills: subSkillsPayload,
        academicYear
      });
      showToast('Skill saved successfully!', SnackbarSeverityType.success);
      onClose();
      onSkillUpdated();
    } catch {
      showToast('Failed to save skill.', SnackbarSeverityType.error);
    } finally {
      setIsSaving(false);
    }
  };

  const getScoringTypeName = (
    scoringType: SubSkillResponse['scoringType']
  ): string => {
    if (typeof scoringType === 'string') {
      return (
        scoringTypes.find((st) => st.id === scoringType)?.name || scoringType
      );
    }
    return scoringType?.name || '';
  };

  const subSkillColumns = getSubSkillColumns(
    handleOpenEditSubSkill,
    handleRemoveSubSkill
  );

  return (
    <DialogModal
      open={open}
      onClose={onClose}
      title='View/Edit Skill'
      maxWidth={false}
      PaperPropsSx={viewEditPaperPropsSx}
      actions={
        <ViewEditActionsContainer>
          <ViewEditCancelButton onClick={onClose} color='primary' variant='outlined'>
            Cancel
          </ViewEditCancelButton>
          <StyledButton onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <CircularProgress size={24} color='inherit' />
            ) : (
              'Save Changes'
            )}
          </StyledButton>
        </ViewEditActionsContainer>
      }
    >
      <ViewEditContentContainer>
        <ViewEditWrapper>
          <ViewEditHeaderRow>
            <TextInputWithIcon
              label='Academic Year'
              placeholder='Academic Year'
              name='academicYear'
              value={academicYear}
              onChange={() => {}}
              fullWidth
              disabled
            />
            <TextInputWithIcon
              label='Skill Name'
              placeholder='Enter Skill Name'
              name='skillName'
              value={skillName}
              onChange={(e) => setSkillName(e.target.value)}
              fullWidth
            />
          </ViewEditHeaderRow>
          <StyledButton onClick={handleOpenAddSubSkill}>
            <Icons.PlusWhite style={{ marginRight: '6px' }} /> Add Sub-Skill
          </StyledButton>
        </ViewEditWrapper>

        <EditSubSkillModal
          open={isEditSubSkillModalOpen}
          onClose={() => setIsEditSubSkillModalOpen(false)}
          onSave={handleSaveEditedSubSkill}
          subSkill={subSkillToEdit}
          scoringTypeOptions={scoringTypes}
          isLoadingScoringTypes={isLoadingScoringTypes}
        />

        <AddSubSkillModal
          open={isAddSubSkillModalOpen}
          onClose={() => setIsAddSubSkillModalOpen(false)}
          onAddSubSkill={handleAddSubSkill}
          scoringTypeOptions={scoringTypes}
          isLoadingScoringTypes={isLoadingScoringTypes}
        />

        <FullWidthBox>
          <DataGrid
            rows={subSkills.map((s) => ({
              ...s,
              scoringTypeName: getScoringTypeName(s.scoringType)
            }))}
            columns={subSkillColumns}
            disableRowSelectionOnClick
            pageSizeOptions={[5, 10, 25]}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 10 } }
            }}
            sx={{
              ...dataGridSx(pallet),
              '& .MuiDataGrid-virtualScroller': { overflowY: 'auto' }
            }}
          />
        </FullWidthBox>
        <ResultCountText variant='body2'>
          {subSkills.length} result{subSkills.length !== 1 ? 's' : ''}
        </ResultCountText>
      </ViewEditContentContainer>
    </DialogModal>
  );
};

export default ViewEditSkillModal;
